import {
  createContext, useContext, useEffect, useMemo, useState, useCallback,
} from 'react';

/**
 * Appearance, the way macOS does it.
 *
 * Three settings — Light, Dark, Auto — where Auto follows the OS and is the
 * default. That is the Apple behaviour: System Settings ships "Auto", and an
 * app that ignores the system appearance feels foreign on the platform.
 *
 * The class is applied to <html> before React mounts (see the inline script in
 * index.html) so a dark-mode user never sees a white flash on load.
 */

const AppearanceContext = createContext(null);

const STORAGE_KEY = 'jobcard.admin.appearance';
const MODES = ['auto', 'light', 'dark'];

function readStored() {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return MODES.includes(value) ? value : 'auto';
  } catch {
    return 'auto';
  }
}

const systemPrefersDark = () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;

export function AppearanceProvider({ children }) {
  const [mode, setMode] = useState(readStored);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // Track the OS setting so "Auto" keeps following it after load.
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query) return undefined;

    const onChange = (event) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const resolved = mode === 'auto' ? (systemDark ? 'dark' : 'light') : mode;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolved === 'dark');
    // Tells the browser to render native controls and scrollbars to match.
    root.style.colorScheme = resolved;

    // Keeps the mobile browser chrome in step with the app's ground colour.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', resolved === 'dark' ? '#000000' : '#f2f2f7');
  }, [resolved]);

  const change = useCallback((next) => {
    setMode(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Private browsing — the choice just will not survive a reload.
    }
  }, []);

  const value = useMemo(() => ({
    mode, resolved, setMode: change, modes: MODES, isDark: resolved === 'dark',
  }), [mode, resolved, change]);

  return <AppearanceContext.Provider value={value}>{children}</AppearanceContext.Provider>;
}

export function useAppearance() {
  const context = useContext(AppearanceContext);
  if (!context) throw new Error('useAppearance must be used inside <AppearanceProvider>');
  return context;
}
